import { Button, Card, CardBody, Flex, Skeleton, Stack, Text } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import {
  PortalInviteStatusDto,
  PortalLifecycleStatus,
  useGetAuthenticatedPortalSourcingEventQuery,
  useGetPortalMyBidsQuery,
  useGetPortalSourcingEventQuery,
  useGetSourcingEventQuery,
} from "../../../../autogen/bff-api";
import { LoginRedirect } from "../../../../common/auth/LoginRedirect";
import { isNotFoundError } from "../../../../common/errors/isNotFoundError";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { PortalLayout } from "../components/PortalLayout";
import { useCurrentMoment } from "../utils/useCurrentMoment";
import { BackToListingLink } from "./BackToListingLink";
import { EventAboutSection } from "./EventAboutSection";
import { EventAwardCriteriaSection } from "./EventAwardCriteriaSection";
import { EventAwardNotificationSection } from "./EventAwardNotificationSection";
import { EventBuyerInfo } from "./EventBuyerInfo";
import { EventContractDetails } from "./EventContractDetails";
import { EventDeadlineHighlight } from "./EventDeadlineHighlight";
import { EventCommunicationSection } from "./communication/EventCommunicationSection";
import { EventDocumentsSection } from "./EventDocumentsSection";
import { EventHero } from "./EventHero";
import { EventChangeLogSection } from "./EventChangeLogSection";
import { EventLifecycleBanner } from "./EventLifecycleBanner";
import { MinimumRequirementsSection } from "./MinimumRequirementsSection";
import { EventProcessTimeline } from "./EventProcessTimeline";
import { EventQualificationSection } from "./EventQualificationSection";
import { ParticipationCta } from "./ParticipationCta";
import { PrivateBidCta } from "./PrivateBidCta";
import { isPrivatePortalEvent, PrivateInviteCta } from "./PrivateInviteCta";
import { PortalNegotiationRoundsSection } from "./PortalNegotiationRoundsSection";
import { RegisterInterestCta } from "./RegisterInterestCta";
import { StartBidCta } from "../bid/StartBidCta";

export const PortalSourcingEventPage = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const displayer = useApiError();
  const now = useCurrentMoment();
  const authState = useAppSelector((state) => state.authState.state);
  const loggedInWithOrg = authState?.type === "LoggedInWithOrgContext";

  const anonymousQuery = useGetPortalSourcingEventQuery(
    { eventId: eventId ?? "" },
    { skip: !eventId || loggedInWithOrg, refetchOnFocus: true }
  );

  const authenticatedQuery = useGetAuthenticatedPortalSourcingEventQuery(
    { eventId: eventId ?? "" },
    { skip: !eventId || !loggedInWithOrg, refetchOnFocus: true }
  );

  const { data, error, isLoading } = loggedInWithOrg ? authenticatedQuery : anonymousQuery;
  const { data: myBids } = useGetPortalMyBidsQuery({}, { skip: !eventId || !loggedInWithOrg });
  const ownBid = myBids?.bids.find((bid) => bid.eventId === eventId);

  const isNotFound = isNotFoundError(error);

  const ownEventQuery = useGetSourcingEventQuery(
    { eventId: eventId ?? "" },
    { skip: !eventId || !loggedInWithOrg || !isNotFound },
  );

  useEffect(() => {
    if (error && !isNotFound && data) {
      displayer.triggerSoft(error);
    }
  }, [displayer, error, isNotFound, data]);

  // A private competition is never served anonymously, so an invited supplier following an invite link
  // while logged out lands on a 404 that only means "log in first". Send them through login and back
  // here instead of telling them the procurement does not exist.
  if (isNotFound && authState?.type === "LoggedOut") {
    return <LoginRedirect />;
  }

  if (isNotFound && loggedInWithOrg && ownEventQuery.isLoading) {
    return (
      <PortalLayout>
        <LoadingSkeleton />
      </PortalLayout>
    );
  }

  if (isNotFound && ownEventQuery.data) {
    return <Navigate to={urls.v2.sourcingEvents.view.go(ownEventQuery.data.id)} replace />;
  }

  if (isNotFound) {
    return (
      <PortalLayout>
        <Stack spacing="6">
          <BackToListingLink />
          <NotFoundCard />
        </Stack>
      </PortalLayout>
    );
  }

  if (error && !data) return <PortalLayout>{displayer.show(error)}</PortalLayout>;

  return (
    <PortalLayout>
      <Stack spacing="6">
        <BackToListingLink />

        {isLoading || !data ? (
          <LoadingSkeleton />
        ) : (
          <>
            <EventHero event={data} now={now} ownBidStatus={ownBid?.status} />
            <EventLifecycleBanner event={data} />
            <EventAwardNotificationSection event={data} />
            <EventDeadlineHighlight event={data} now={now} />
            {loggedInWithOrg && <PrivateInviteCta event={data} />}
            {/* An invitation to a private competition is an invitation to bid — accepting it first
                was a step that bought nothing, so the bid CTA appears for anyone invited. Declining
                is still on the table, and hides the CTA. */}
            {loggedInWithOrg &&
              isPrivatePortalEvent(data) &&
              data.lifecycle.status === PortalLifecycleStatus.Open &&
              data.myInviteStatus != null &&
              data.myInviteStatus !== PortalInviteStatusDto.NotInvited &&
              data.myInviteStatus !== PortalInviteStatusDto.Declined && (
                <PrivateBidCta event={data} variant="full" />
              )}
            <ParticipationCta event={data} />
            <RegisterInterestCta event={data} />
            <StartBidCta event={data} />
            {loggedInWithOrg && <PortalNegotiationRoundsSection event={data} />}
            {/* The timeline is built from public-procurement milestones (announcement, standstill…).
                A private competition has only the bid deadline, which the card above already shows. */}
            {!isPrivatePortalEvent(data) && <EventProcessTimeline event={data} now={now} />}
            <EventChangeLogSection event={data} />
            <EventAboutSection event={data} />
            <EventAwardCriteriaSection event={data} />
            <EventQualificationSection event={data} />
            <MinimumRequirementsSection requirements={data.minimumRequirements ?? []} />
            <EventDocumentsSection event={data} />
            <EventCommunicationSection event={data} />
            <EventContractDetails event={data} />
            <EventBuyerInfo event={data} />
          </>
        )}
      </Stack>
    </PortalLayout>
  );
};

const LoadingSkeleton = () => (
  <Stack spacing="6">
    <Skeleton height="80px" rounded="md" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="100px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="200px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="180px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
    <Skeleton height="160px" rounded="xl" startColor="smBackground" endColor="smBackgroundTertiary" />
  </Stack>
);

const NotFoundCard = () => {
  const { t } = useTranslation();
  const portalUrls = usePortalEventUrls();
  return (
    <Card variant="outline" rounded="xl">
      <CardBody py="20">
        <Flex direction="column" align="center" justify="center" gap="4">
          <Text color="smMuted" textAlign="center">
            {t("Procurement not found or no longer available")}
          </Text>
          <Button as={RouterLink} to={portalUrls.list} variant="outline" colorScheme="gray">
            {t("Back to listing")}
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );
};
